import {
  ReviewAction,
  withdrawalReviewsApi as api,
} from "api/admin/withdrawalReviews";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { showDanger, showSuccess } from "utilities/misc";
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingRows,
  Pager,
  ReasonDialog,
  StatusPill,
  errorMessage,
  fieldClass,
  formatDate,
  formatLabel,
} from "../access-control/components";
import WithdrawalRules from "./WithdrawalRules";
const statuses = [
  "all",
  "pending_delay",
  "pending_liveness",
  "pending_compliance",
  "approved",
  "releasing",
  "rejecting",
  "rejected",
  "released",
];
const actions: Record<ReviewAction, { label: string; description: string }> = {
  "clear-flag": {
    label: "Approve & clear flag",
    description:
      "Approve compliance review. Held withdrawals may be released once the remaining liveness and delay requirements are satisfied.",
  },
  escalate: {
    label: "Escalate",
    description:
      "Record an escalation in the audit history. This does not change the payment or review status.",
  },
  reverse: {
    label: "Reject & reverse",
    description:
      "Reject this review and cancel/refund withdrawals that are still held. Released payments cannot be reversed here.",
  },
};
export default function WithdrawalReviews() {
  const { can } = useAdminAccess();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const reviewId = Number(params.get("review")) || null;
  const [section, setSection] = useState(
    can("transactions.withdrawalReviews.view") ? "reviews" : "rules",
  );
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<ReviewAction | null>(null);
  const [busy, setBusy] = useState(false);
  const list = useQuery(
    ["withdrawal-reviews", status, page],
    () => api.list(status, page),
    {
      enabled:
        section === "reviews" && can("transactions.withdrawalReviews.view"),
      retry: false,
    },
  );
  const detail = useQuery(
    ["withdrawal-review", reviewId],
    () => api.detail(reviewId!),
    {
      enabled:
        !!reviewId &&
        section === "reviews" &&
        can("transactions.withdrawalReviews.view"),
      retry: false,
    },
  );
  const review = detail.data?.review;
  const selectReview = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("review", String(id));
    else next.delete("review");
    setParams(next);
  };
  const confirm = async (reason: string) => {
    if (!reviewId || !action || busy) return;
    setBusy(true);
    try {
      const result = await api.act(reviewId, action, reason);
      setAction(null);
      showSuccess(`Review updated: ${formatLabel(result.review.status)}`);
      await Promise.all([
        client.invalidateQueries("withdrawal-reviews"),
        client.invalidateQueries(["withdrawal-review", reviewId]),
        client.invalidateQueries("flaggedTransactions"),
      ]);
    } catch (error) {
      showDanger(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Container>
      <AdminSidebar />
      <Main className="pb-10 mx-5 mt-5 text-left">
        <h1 className="text-2xl font-semibold">Withdrawal reviews</h1>
        <nav
          aria-label="Withdrawal review sections"
          className="flex gap-2 my-6"
        >
          {can("transactions.withdrawalReviews.view") && (
            <Button
              variant={section === "reviews" ? "primary" : "secondary"}
              onClick={() => setSection("reviews")}
            >
              Reviews
            </Button>
          )}
          {can("transactions.withdrawalReviewRules.view") && (
            <Button
              variant={section === "rules" ? "primary" : "secondary"}
              onClick={() => setSection("rules")}
            >
              Rules & change requests
            </Button>
          )}
        </nav>
        {section === "rules" ? (
          <WithdrawalRules />
        ) : (
          <Card
            title="Review queue"
            action={
              <label className="text-sm">
                Status
                <select
                  className={fieldClass}
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  {statuses.map((v) => (
                    <option key={v} value={v}>
                      {formatLabel(v)}
                    </option>
                  ))}
                </select>
              </label>
            }
          >
            {list.isError ? (
              <ErrorState error={list.error} retry={list.refetch} />
            ) : list.isLoading ? (
              <LoadingRows />
            ) : !list.data?.reviews.length ? (
              <EmptyState
                title="No reviews"
                body="There are no withdrawal reviews matching this status."
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr>
                        {[
                          "Review / user",
                          "Status",
                          "Liveness",
                          "Reason",
                          "Created",
                          "",
                        ].map((h) => (
                          <th className="p-4" key={h}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {list.data.reviews.map((row) => (
                        <tr className="border-t" key={row.id}>
                          <td className="p-4">
                            #{row.id} / User #{row.userID}
                          </td>
                          <td className="p-4">
                            <StatusPill status={row.status} />
                          </td>
                          <td className="p-4">
                            {formatLabel(row.livenessStatus)}
                          </td>
                          <td className="max-w-xs p-4 break-words">
                            {row.reason || "—"}
                          </td>
                          <td className="p-4">{formatDate(row.createdAt)}</td>
                          <td className="p-4">
                            <Button
                              variant="secondary"
                              onClick={() => selectReview(row.id)}
                            >
                              Review
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  page={page}
                  totalPages={Math.ceil(list.data.total / 25)}
                  setPage={setPage}
                />
              </>
            )}
          </Card>
        )}
        <Drawer
          open={!!reviewId && section === "reviews"}
          title={`Withdrawal review #${reviewId}`}
          wide
          close={() => !busy && selectReview(null)}
        >
          {detail.isError ? (
            <ErrorState error={detail.error} retry={detail.refetch} />
          ) : detail.isLoading ? (
            <LoadingRows />
          ) : (
            review && (
              <div className="space-y-6">
                <div>
                  <StatusPill status={review.status} />
                  <p className="mt-3">
                    User #{review.userID} · Compliance:{" "}
                    {formatLabel(review.complianceStatus)} · Liveness:{" "}
                    {formatLabel(review.livenessStatus)}
                  </p>
                  <p className="mt-2">{review.reason}</p>
                  {review.rejectionReason && (
                    <p>Rejection: {review.rejectionReason}</p>
                  )}
                </div>
                {can("transactions.withdrawalReviews.approve") && (
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(actions) as ReviewAction[]).map((key) => (
                      <Button
                        key={key}
                        disabled={
                          busy ||
                          detail.isFetching ||
                          (key !== "escalate" &&
                            [
                              "rejected",
                              "released",
                              "releasing",
                              "rejecting",
                            ].includes(review.status))
                        }
                        variant={key === "reverse" ? "danger" : "secondary"}
                        onClick={() => setAction(key)}
                      >
                        {actions[key].label}
                      </Button>
                    ))}
                  </div>
                )}
                {(
                  [
                    ["transactions", "Transactions"],
                    ["otcTransactions", "OTC transactions"],
                  ] as const
                ).map(([key, label]) => (
                  <section key={key}>
                    <h3 className="mb-3 font-semibold">{label}</h3>
                    {!detail.data?.[key]?.length ? (
                      <p className="text-sm text-gray-500">None linked.</p>
                    ) : (
                      <ul className="divide-y">
                        {detail.data[key].map((tr) => (
                          <li className="py-3 text-sm" key={tr.id}>
                            #{tr.id} · {tr.amount}{" "}
                            {tr.currency || "(currency unavailable)"} ·{" "}
                            {formatLabel(tr.status)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
                <section>
                  <h3 className="font-semibold">Audit history</h3>
                  <ul className="divide-y">
                    {detail.data?.auditLogs.map((log) => (
                      <li className="py-3 text-sm" key={log.id}>
                        {formatLabel(log.actionType)} ·{" "}
                        {formatDate(log.timestamp)}
                        <p>{log.reason}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            )
          )}
        </Drawer>
        <ReasonDialog
          open={!!action}
          title={action ? actions[action].label : ""}
          description={action ? actions[action].description : ""}
          confirmLabel={action ? actions[action].label : "Confirm"}
          danger={action === "reverse"}
          busy={busy}
          close={() => !busy && setAction(null)}
          confirm={confirm}
        />
      </Main>
    </Container>
  );
}
